import { type FC, type ReactNode } from "react";

import { cx } from "@emotion/css";
import { Colors } from "@snapchat/snap-design-system";
import { WarningIcon } from "@snapchat/snap-design-system-icons";

import { DestinationTypeBm, type LensDestination } from "../../../gql-generated/types";
import { Flexbox } from "../../../shared/components";
import { lensStatusMap } from "../../state";

import { IconWrapper, minWidth, noMargin } from "./LensDestinationRow.styles";
import { LensDestinationRowContainer, LensDestinationText } from "./LensDestinationsTooltip.styles";
import { LensStatusIndicator } from "./LensStatus.styles";
import { AdAccountTitle, OwnProfileTitle, PublicProfileTitle, UserProfileTitle } from "./messages.generated";

export interface LensDestinationRowProps {
    destination: LensDestination;
    userId: string;
    hasRestrictions?: boolean;
}

const makeProfileNames: Partial<Record<DestinationTypeBm, (profileName?: string, isSameUser?: boolean) => ReactNode>> =
    {
        [DestinationTypeBm.DestinationTypeAdAccount]: () => <AdAccountTitle />,
        [DestinationTypeBm.DestinationTypePublicProfile]: (profileName?: string) =>
            profileName ?? <PublicProfileTitle />,
        [DestinationTypeBm.DestinationTypeUser]: (_, isSameUser?: boolean) =>
            isSameUser ? <OwnProfileTitle /> : <UserProfileTitle />,
    };

export const LensDestinationRow: FC<LensDestinationRowProps> = ({ destination, userId, hasRestrictions = false }) => {
    const { status, destinationType, publicProfileMetadata, id } = destination;

    const { name: statusName, color: indicationColor } = lensStatusMap[status];

    const profileName = makeProfileNames[destinationType]?.(publicProfileMetadata?.displayName, id === userId);

    return (
        <LensDestinationRowContainer data-testid="destination-row">
            <LensDestinationText>{profileName}</LensDestinationText>

            <Flexbox gap="4px" className={cx(minWidth, noMargin)} alignItems="center">
                {hasRestrictions ? (
                    <>
                        <IconWrapper>
                            <WarningIcon fill={Colors.Orange.V200} />
                        </IconWrapper>
                        <LensDestinationText color={Colors.Orange.V200}>Limited</LensDestinationText>
                    </>
                ) : (
                    <>
                        <IconWrapper>
                            <LensStatusIndicator marginRight={0} size={12} background={indicationColor} />
                        </IconWrapper>
                        <LensDestinationText>{statusName}</LensDestinationText>
                    </>
                )}
            </Flexbox>
        </LensDestinationRowContainer>
    );
};
